// The Stimulus twin of the HAML `gl_alert` helper (app/helpers/gl_alert_helper.rb): one
// builder for an alert a controller paints at runtime, so no controller spells out the
// .gl-alert markup by hand (#19800 wave 8, F1). Built from DOM nodes, never an HTML
// string, so a server-sent sentence is always text.
//
//   buildGlAlert({ tone: "danger", icon: "ph-warning-circle", body: ["Could not save."] })
//   // => <div class="gl-alert gl-alert--danger" role="status">
//   //      <i class="ph-fill ph-warning-circle gl-alert-icon" aria-hidden="true"></i>
//   //      <div class="gl-alert-content">Could not save.</div></div>
//
// Options mirror gl_alert:
//   tone        "info" | "success" | "warning" | "danger", or null for the neutral alert
//   icon        a Phosphor glyph name, or null for an alert with no icon
//   iconWeight  "fill" (default) or "regular" (the plain `ph` outline glyph)
//   title       a heading in the content column (layout "column" only)
//   body        strings (inserted as text) and/or Nodes
//   layout      "column" (default) wraps the body in .gl-alert-content; "row" puts it
//               straight in the alert's flex row
//   action      a Node placed after the body (a button, a dismiss control)
//   className   extra classes on the alert, after the tone
//   role        defaults to "status"; null leaves the role off (a live child holds it)
//   attributes  more attributes, set after class and role, in order
//
// gl_alert joins a row body with a newline for hand-written HAML; DOM nodes need no
// whitespace, so this builder adds none.
const TONES = ["info", "success", "warning", "danger"]
const ICON_WEIGHTS = { fill: "ph-fill", regular: "ph" }
const LAYOUTS = ["column", "row"]

export function buildGlAlert({
  tone = null,
  icon = null,
  iconWeight = "fill",
  title = null,
  body = [],
  layout = "column",
  action = null,
  className = null,
  role = "status",
  attributes = {},
} = {}) {
  if (tone !== null && !TONES.includes(tone)) throw new Error(`gl_alert: unknown tone ${tone}`)
  if (!LAYOUTS.includes(layout)) throw new Error(`gl_alert: unknown layout ${layout}`)
  if (layout === "row" && title) throw new Error("gl_alert: a title needs layout column")

  const alert = document.createElement("div")
  alert.className = ["gl-alert", tone && `gl-alert--${tone}`, className].filter(Boolean).join(" ")
  if (role) alert.setAttribute("role", role)
  Object.entries(attributes).forEach(([name, value]) => alert.setAttribute(name, value))

  if (icon) alert.append(buildIcon(icon, iconWeight))
  if (layout === "row") {
    alert.append(...body)
  } else {
    alert.append(buildContent(title, body))
  }
  if (action) alert.append(action)
  return alert
}

// The canonical × that removes its alert (never Bootstrap's btn-close).
export function buildGlAlertDismiss({ label = "Close", onDismiss } = {}) {
  const button = document.createElement("button")
  button.type = "button"
  button.className = "gl-alert-dismiss"
  button.setAttribute("aria-label", label)
  const glyph = document.createElement("i")
  glyph.className = "ph ph-x"
  glyph.setAttribute("aria-hidden", "true")
  button.append(glyph)
  if (onDismiss) button.addEventListener("click", onDismiss)
  return button
}

function buildIcon(icon, weight) {
  const weightClass = ICON_WEIGHTS[weight]
  if (!weightClass) throw new Error(`gl_alert: unknown icon weight ${weight}`)

  const el = document.createElement("i")
  el.className = `${weightClass} ${icon} gl-alert-icon`
  el.setAttribute("aria-hidden", "true")
  return el
}

function buildContent(title, body) {
  const content = document.createElement("div")
  content.className = "gl-alert-content"
  if (title) {
    const heading = document.createElement("h4")
    heading.className = "gl-alert-title"
    heading.textContent = title
    content.append(heading)
  }
  content.append(...body)
  return content
}
